/*****************************************************************
 *  CUSTOMER BUTLER - THE PAGE GATE                                *
 *  File: apps/web/src/pages/customer/CustomerAccessGate.js        *
 *  What it does: stands in front of every Customer Butler page    *
 *  (clean, classic, properties, CRM Setup). Asks the server ONCE  *
 *  per page what this person's Customer Butler level is, then     *
 *  either shows the page (sharing the level with every button     *
 *  on it) or a plain-English page saying why not.                 *
 *  Reads: GET /api/customer/access/me (customerAccessView.js).    *
 *  Writes: nothing.                                               *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE WORDS                                          *
 *  SECTION 2 - THE PLAIN PAGES (no access, setup, no signal)      *
 *  SECTION 3 - THE GATE                                           *
 *****************************************************************/

// WHY (2026-10-14, James's Honest UI rule, via Fable): a person with no
// Customer Butler access who typed the address used to see "The customer list
// could not be loaded. Check the connection and try again." That was not
// true. They now see why, and who can change it.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from './customerIcons';
import { Notice, QuietLine } from './customerParts';
import { useButlerLook, useCustomerSkin, SKIN_HR } from './customerLook';
import { HrPageFrame, HrBanner } from './HrChrome';
import {
  askMyCustomerAccess, describeWhatLevelShows, CustomerAccessContext, LEVEL_NONE,
} from './customerAccessView';

/*****************************************************************
 *  SECTION 1 - THE WORDS                                          *
 *****************************************************************/

export const NO_ACCESS_WORDS = "You don't have access to Customer Butler yet. "
  + 'Ask the office to set your Customer Butler access on the HR Team page.';

export const SETUP_NOT_YOURS_WORDS = 'CRM Setup is for Customer Butler managers. '
  + 'Ask the office if you need Manager access on the HR Team page.';

export const NO_SIGNAL_WORDS = 'There is no signal, so Customer Butler could not check your access. '
  + 'Try again when you have signal.';

export const TROUBLE_WORDS = 'Customer Butler could not check your access just now. Try again in a minute.';

export const NO_SIGNAL_NOTE = 'No signal: showing what your access allowed last time on this device.';

/*****************************************************************
 *  SECTION 2 - THE PLAIN PAGES                                    *
 *****************************************************************/

/* ---- PlainFrame({ children }) -- the Butler look, no module menu ----
   WHY (2026-10-15, James): in the HR look the suite bars are gone on
   Customer Butler addresses, so the plain pages wear HR's header row
   (with its "<- Hub") instead, and no places row.               */
function PlainFrame({ children }) {
  const [skinName] = useCustomerSkin();
  useButlerLook();

  if (skinName === SKIN_HR) {
    return <HrPageFrame showPlaces={false}>{children}</HrPageFrame>;
  }

  return (
    <div className="cb-page bc-page">
      <h1 className="cb-title">Customer Butler</h1>
      {children}
    </div>
  );
}

/* ---- ExplainPage({ tone, icon, words, backTo, backWords }) ------
   One sentence in a notice box, and one way back.               */
function ExplainPage({ tone, icon, words, backTo, backWords }) {
  const [skinName] = useCustomerSkin();

  if (skinName === SKIN_HR) {
    return (
      <PlainFrame>
        <HrBanner tone={tone === 'neutral' ? 'quiet' : tone} icon={icon}>{words}</HrBanner>
        <Link className="cb-hr-back" to={backTo}><Icon name="arrowleft" /> {backWords}</Link>
      </PlainFrame>
    );
  }

  return (
    <PlainFrame>
      <Notice tone={tone} icon={icon}>{words}</Notice>
      <Link className="cb-back" to={backTo}><Icon name="back" /> {backWords}</Link>
    </PlainFrame>
  );
}

/*****************************************************************
 *  SECTION 3 - THE GATE                                           *
 *****************************************************************/

/* ---- CustomerAccessGate({ needs, children }) --------------------
   In:  needs  'open' (any Customer Butler page) or 'setup' (CRM
               Setup: Manager only)
        children  the page to show when allowed
   Out: the page inside the shared level, or a plain page that says
        why not. Asks the server once each time the page opens, so a
        change on the HR Team page shows on the next page opened.
   Never: decides access itself; the server's level is the answer. */
export function CustomerAccessGate({ needs, children }) {
  const auth = useAuth();
  const userId = auth && auth.user ? auth.user.id : null; // whose device memory to use
  const [answer, setAnswer] = useState(null);             // null = still asking

  useEffect(() => {
    let isStillShowing = true; // a late answer must not touch a closed page

    askMyCustomerAccess(userId).then((serverAnswer) => {
      if (isStillShowing) {
        setAnswer(serverAnswer);
      }
    });

    return () => {
      isStillShowing = false;
    };
  }, [userId]);

  if (!answer) {
    return (
      <PlainFrame>
        <QuietLine icon="clock">Checking your Customer Butler access</QuietLine>
      </PlainFrame>
    );
  }

  if (answer.state === 'no-signal') {
    return <ExplainPage tone="warn" icon="warn" words={NO_SIGNAL_WORDS} backTo="/" backWords="Hub" />;
  }

  if (answer.state === 'trouble') {
    return <ExplainPage tone="warn" icon="warn" words={TROUBLE_WORDS} backTo="/" backWords="Hub" />;
  }

  const shown = describeWhatLevelShows(answer.level || LEVEL_NONE); // what this level may see

  if (!shown.mayOpen) {
    return <ExplainPage tone="neutral" icon="lock" words={NO_ACCESS_WORDS} backTo="/" backWords="Hub" />;
  }

  const needsSetup = needs === 'setup'; // CRM Setup is Manager only

  if (needsSetup && !shown.mayChangeSetup) {
    return (
      <ExplainPage tone="neutral" icon="lock" words={SETUP_NOT_YOURS_WORDS} backTo="/customers" backWords="Customers" />
    );
  }

  const isFromMemory = answer.state === 'no-signal-remembered'; // offline, last known level

  return (
    <CustomerAccessContext.Provider value={shown}>
      {isFromMemory ? <p className="cb-quiet cb-offline-note">{NO_SIGNAL_NOTE}</p> : null}
      {children}
    </CustomerAccessContext.Provider>
  );
}

export default CustomerAccessGate;
